/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { Fragment } from 'react';

import get from 'lodash/get';

import { Checkbox } from '../../../common';
import ArrowTooltip from '../../../common/Tooltip/Tooltip';
import { CHECKED_COLUMN_NAME } from '../../constants';
import { setOrder } from '../../helpers/filterAndSort';
import { ISetTableSortPayload } from '../../types/tableTypes';
import { getIsFastBasketPage } from '../../utils/tableUtils';
import Filter, { IFilterSort, SortEnum } from '../Filters/Filter';
import { CellStyled, CheckboxFilterStyled, CheckboxTableStyled } from './styled/ColumnsStyled';

/**
 * @description Props for checked column
 * @interface ICheckboxColumn
 * @property {IFilterSort} current sort field and direction
 * @property {boolean} checkbox flag
 * @property {(e: string) => void} onSort - sorting method
 * @property {(e: boolean, row?: RowInfo) => void} onChange - checkbox change method
 */
export interface ICheckboxColumn {
    checkedAll: boolean;
    disableCheckedAll?: boolean;
    disableCheckedAllMessage?: string;
    onChange: (e: boolean, row?: any) => void;
    onSort: (e: ISetTableSortPayload) => void;
    sort: IFilterSort;
    unsortable?: boolean;
}

/**
 * @description Cell constructor return function
 * @param {(e: boolean, row?: RowInfo) => void} onChange - checkbox change method
 */
const CellConstruct = (onChange: (e: boolean, data?: any) => void) => (data: any) => {

    const isFastBasketPage = getIsFastBasketPage();

    const row = get(data, 'row');
    const onChangeHandler = (e: boolean) => {
        onChange(e, row);
    };
    return (
        <CheckboxTableStyled>
            {row.original.expandedColumn ?
                (<div/>)
                :
                (<Checkbox
                    checked={isFastBasketPage ? row.original.fastActionChecked : row.original.checked}
                    onChange={onChangeHandler}
                />)
            }
        </CheckboxTableStyled>
    );
};
/**
 * @description Header construct return function
 * @interface ICheckboxColumn
 */
const HeaderConstruct = ({
                             disableCheckedAll,
                             disableCheckedAllMessage,
                             checkedAll,
                             onChange,
                             onSort,
                             sort,
                             unsortable,
                         }: ICheckboxColumn) => () => (
    <CellStyled>
        <Filter
            isSortable={!disableCheckedAll && !unsortable}
            sort={(sort.property === CHECKED_COLUMN_NAME) ?
                sort.direction : SortEnum.default
            }
            onSort={() => onSort({ property: CHECKED_COLUMN_NAME, order: setOrder(sort, CHECKED_COLUMN_NAME) })}
        >
            {
                disableCheckedAll ?
                    <ArrowTooltip
                        disableHoverListener={!disableCheckedAllMessage}
                        message={disableCheckedAllMessage}
                        placement="left-start"
                        title=""
                    >
                        <CheckboxFilterStyled>
                            <Checkbox
                                disabled
                                checked={checkedAll}
                            />
                        </CheckboxFilterStyled>
                    </ArrowTooltip>
                    :
                    <CheckboxFilterStyled>
                        <Checkbox
                            checked={checkedAll}
                            onChange={onChange}
                        />
                    </CheckboxFilterStyled>
            }
        </Filter>
    </CellStyled>
);
/**
 * @description setting the checked column
 * @interface ICheckboxColumn
 */
const CheckedColumn = ({
                           checkedAll,
                           disableCheckedAll,
                           disableCheckedAllMessage,
                           onChange,
                           onSort,
                           sort,
                           unsortable,
                       }: ICheckboxColumn) => ({
    accessor: CHECKED_COLUMN_NAME,
    Cell: CellConstruct(onChange),
    className: 'first-of-type',
    fixed: 'left',
    Footer: () => <Fragment/>,
    Header: HeaderConstruct({
        checkedAll,
        disableCheckedAll,
        disableCheckedAllMessage,
        onChange,
        onSort,
        sort,
        unsortable,
    }),
    resizable: false,
    rowCount: 1,
    width: 48,
});

export default CheckedColumn;
